/**
 * Rider Service API Client Utility
 * Base HTTP client for making requests to the Aboosto Rider Service API
 */

import { ApiResponse } from './types';
import { RIDER_API_BASE_URL } from '../config';

export { RIDER_API_BASE_URL } from '../config';

export interface RiderRequestConfig extends RequestInit {
  params?: Record<string, any>;
}

export class RiderApiClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;
  private authToken: string | null = null;

  constructor(baseURL: string = RIDER_API_BASE_URL, headers: Record<string, string> = {}) {
    this.baseURL = baseURL;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...headers,
    };
  }

  /**
   * Set authentication token
   */
  setAuthToken(token: string): void {
    this.authToken = token;
    this.defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  /**
   * Clear authentication token
   */
  clearAuthToken(): void {
    this.authToken = null;
    delete this.defaultHeaders['Authorization'];
  }

  /**
   * Get current auth token
   */
  getAuthToken(): string | null {
    return this.authToken;
  }

  /**
   * Build URL with query parameters
   */
  private buildURL(endpoint: string, params?: Record<string, any>): string {
    // Check if baseURL is absolute (starts with http:// or https://)
    const isAbsoluteBase = /^https?:\/\//i.test(this.baseURL);

    let url: URL;
    if (isAbsoluteBase) {
      // Use URL constructor for absolute base URLs
      url = new URL(endpoint, this.baseURL);
    } else {
      // For relative base URLs, construct the path manually
      const basePath = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
      const endpointPath = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
      const fullPath = `${basePath}${endpointPath}`;
      // Use window.location.origin as base for relative URLs
      url = new URL(fullPath, window.location.origin);
    }

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => url.searchParams.append(key, String(v)));
          } else {
            url.searchParams.append(key, String(value));
          }
        }
      });
    }

    // For relative base URLs, return just the pathname + search
    if (!isAbsoluteBase) {
      return url.pathname + url.search;
    }

    return url.toString();
  }

  /**
   * Make HTTP request
   */
  private async request<T>(
    endpoint: string,
    config: RiderRequestConfig & { responseType?: 'json' | 'blob' } = {}
  ): Promise<T> {
    const { params, headers, responseType = 'json', ...restConfig } = config;
    const url = this.buildURL(endpoint, params);

    try {
      // Prepare headers, but don't override Content-Type if body is FormData
      const requestHeaders: Record<string, string> = { ...this.defaultHeaders };

      // Merge with custom headers first
      Object.assign(requestHeaders, headers || {});

      // If body is FormData, remove Content-Type to let browser set it with boundary
      // This must be done AFTER merging custom headers to ensure it's not overridden
      if (restConfig.body instanceof FormData) {
        delete requestHeaders['Content-Type'];
      }

      // If there's no body, remove Content-Type header (some APIs don't like Content-Type without body)
      if (!restConfig.body) {
        delete requestHeaders['Content-Type'];
      }

      const response = await fetch(url, {
        ...restConfig,
        headers: requestHeaders,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || errorData.error || `HTTP Error: ${response.status} ${response.statusText}`
        );
      }

      // Handle different response types
      if (responseType === 'blob') {
        const blob = await response.blob();
        return blob as unknown as T;
      }

      const data: T = await response.json();
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred');
    }
  }

  /**
   * GET request
   */
  async get<T>(
    endpoint: string,
    params?: Record<string, any>,
    config?: RiderRequestConfig & { responseType?: 'json' | 'blob' }
  ): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', params, ...config });
  }

  /**
   * POST request
   */
  async post<T>(
    endpoint: string,
    data?: any,
    config?: RiderRequestConfig & { responseType?: 'json' | 'blob' }
  ): Promise<T> {
    const { params, ...restConfig } = config || {};
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined),
      params,
      ...restConfig,
    });
  }

  /**
   * PUT request
   */
  async put<T>(
    endpoint: string,
    data?: any,
    config?: RiderRequestConfig & { responseType?: 'json' | 'blob' }
  ): Promise<T> {
    const { params, ...restConfig } = config || {};
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined),
      params,
      ...restConfig,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  /**
   * Update base URL
   */
  setBaseURL(baseURL: string): void {
    this.baseURL = baseURL;
  }

  /**
   * Update default headers
   */
  setHeaders(headers: Record<string, string>): void {
    this.defaultHeaders = {
      ...this.defaultHeaders,
      ...headers,
    };
  }
}

// Export singleton instance
export const riderApiClient = new RiderApiClient();
